import type { AlertKind, FulfillmentSummary, FulfillmentTarget, LineFulfillment, OrderLine } from '@fitting-room/contracts'
import { ALERT_KINDS, effectiveEta, fulfillmentPath, nextFulfillment, previousFulfillment } from '@fitting-room/contracts/fulfillment'
import { BOUTIQUE_LOCALE, boutiqueDayOf } from './boutique'
import { formatDay } from './format'

/**
 * The words for a line on its way to the customer, and for the alerts, kept
 * out of the components so they can be tested in `node` like the rest of
 * `lib/`. Which step comes next is `@fitting-room/contracts/fulfillment` —
 * the same module the API refuses every other step with, so the one button a
 * line shows is never a button the API would answer with a 409.
 */

export const ALERT_KIND_LABEL: Record<AlertKind, string> = {
  supplier_not_ordered: 'Not ordered yet',
  no_eta: 'No ETA',
  eta_too_late: 'ETA too close to the wedding',
  eta_passed: 'ETA passed',
  arrived_not_picked_up: 'Waiting for pickup',
  wedding_soon: 'Wedding soon',
  on_hold_too_long: 'On hold too long',
  balance_due: 'Balance due',
  follow_up_due: 'Follow-up due',
}

/** The order the kind filters are drawn in: `docs/22`'s table, top to bottom. */
export const ALERT_KIND_ORDER: readonly AlertKind[] = ALERT_KINDS

/** The one button that moves a line on. */
export const ADVANCE_LABEL: Record<FulfillmentTarget, string> = {
  ordered: 'Mark ordered',
  shipped: 'Mark shipped',
  arrived: 'Mark arrived',
  picked_up: 'Mark picked up',
}

/** What the step's date field is called in the dialog that marks it. */
export const STEP_DAY_LABEL: Record<FulfillmentTarget, string> = {
  ordered: 'Ordered on',
  shipped: 'Shipped on',
  arrived: 'Arrived on',
  picked_up: 'Picked up on',
}

/** A step, as a sentence's end: "back to arrived". */
export const STEP_PHRASE: Record<LineFulfillment, string> = {
  none: 'not tracked',
  to_order: 'to order',
  ordered: 'ordered',
  shipped: 'shipped',
  arrived: 'arrived',
  on_hold: 'on hold',
  picked_up: 'picked up',
}

type LineShape = Pick<OrderLine, 'kind' | 'fulfillment' | 'arrivedAt'>

/** The step the line's button moves it to, or `null` for a line with none left. */
export function nextStep(line: LineShape): FulfillmentTarget | null {
  const next = nextFulfillment(line)
  return next && next !== 'none' && next !== 'to_order' && next !== 'on_hold' ? next : null
}

/** The step an undo goes back to, or `null` where the line started. */
export function previousStep(line: LineShape): LineFulfillment | null {
  return previousFulfillment(line)
}

export function isSupplierLine(line: LineShape): boolean {
  return fulfillmentPath(line) === 'supplier'
}

export function isStockLine(line: LineShape): boolean {
  return fulfillmentPath(line) === 'stock'
}

export interface StepStamp {
  readonly step: LineFulfillment
  readonly label: string
  /** `YYYY-MM-DD` in the showroom, absent for a step not reached. */
  readonly day?: string
}

/**
 * The line's steps with the day each was reached, for the row under it:
 * "Ordered Oct 1 · Shipped Oct 3 · Arrived — · Picked up —". The steps
 * before where it is now that have no stamp (none should) read as reached.
 */
export function stepStamps(line: Pick<OrderLine, 'kind' | 'fulfillment' | 'arrivedAt' | 'supplierOrderedAt' | 'shippedAt' | 'pickedUpAt' | 'createdAt'>): StepStamp[] {
  const day = (iso: string | undefined) => (iso ? boutiqueDayOf(iso) : undefined)
  const path = fulfillmentPath(line)
  if (path === 'supplier') {
    return [
      { step: 'ordered', label: 'Ordered', ...opt(day(line.supplierOrderedAt)) },
      { step: 'shipped', label: 'Shipped', ...opt(day(line.shippedAt)) },
      { step: 'arrived', label: 'Arrived', ...opt(day(line.arrivedAt)) },
      { step: 'picked_up', label: 'Picked up', ...opt(day(line.pickedUpAt)) },
    ]
  }
  if (path === 'stock') {
    return [
      { step: 'on_hold', label: 'On hold', ...opt(day(line.createdAt)) },
      { step: 'picked_up', label: 'Picked up', ...opt(day(line.pickedUpAt)) },
    ]
  }
  return []
}

function opt(day: string | undefined): { day?: string } {
  return day ? { day } : {}
}

/** The ETA that counts, and whose it is. */
export function etaOf(line: Pick<OrderLine, 'manualEta' | 'systemEta'>): { day: string; source: 'manual' | 'system' } | null {
  const day = effectiveEta(line)
  if (!day) return null
  return { day, source: line.manualEta ? 'manual' : 'system' }
}

/**
 * Where an order's lines are, for its row in the list: "2 of 3 picked up ·
 * next ETA Jan 10, 2027". `null` for an order with nothing to take home.
 */
export function fulfillmentLine(summary: FulfillmentSummary): string | null {
  if (summary.lines === 0) return null
  const parts: string[] = []
  if (summary.pickedUp === summary.lines) parts.push(summary.lines === 1 ? 'Picked up' : 'All picked up')
  else if (summary.pickedUp > 0) parts.push(`${summary.pickedUp} of ${summary.lines} picked up`)
  if (summary.ready > 0) parts.push(summary.ready === summary.lines ? 'ready for pickup' : `${summary.ready} ready for pickup`)
  if (summary.nextEta) parts.push(`next ETA ${formatDay(summary.nextEta, BOUTIQUE_LOCALE)}`)
  if (parts.length === 0) parts.push(summary.lines === 1 ? 'On its way' : `${summary.lines} on their way`)
  const text = parts.join(' · ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}
